import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, CreditCard, Percent, CalendarClock, ShieldCheck, ArrowRight, ExternalLink, Phone } from "lucide-react";
import { FINANCING, COMPANY, ASSETS } from "../mock";
import { SectionHeading, ProcessSteps } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

const ICONS = { CreditCard, Percent, CalendarClock, ShieldCheck };

export default function Financing() {
  useScrollReveal();
  useSEO({
    title: "Financing Options for Screen & Pool Enclosures | Miller & Miller Services",
    description: "Flexible financing through Synchrony for your new pool cage or patio enclosure in Melbourne, FL. Apply online for competitive terms and convenient monthly payments.",
    keywords: ["pool enclosure financing", "screen enclosure financing Melbourne FL", "Synchrony financing"],
    path: "/financing",
  });

  const ApplyBtn = ({ className = "" }) => (
    <a
      href={FINANCING.applyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 h-13 h-12 px-8 rounded-md bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold transition-colors ${className}`}
    >
      Apply Now with {FINANCING.provider} <ExternalLink className="h-4 w-4" />
    </a>
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool3} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">Financing</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">{FINANCING.headline}</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">{FINANCING.intro}</p>
          <div className="mt-7"><ApplyBtn /></div>
        </div>
      </section>

      {/* Perks */}
      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeading center eyebrow="Why Finance With Us" title="Affordable Payments, Beautiful Results" subtitle={`Financing is provided by ${FINANCING.provider}, a trusted national consumer lender - so you can transform your backyard now and pay over time.`} />
          <div className="mt-8 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {FINANCING.perks.map((p, i) => {
              const Icon = ICONS[p.icon] || CreditCard;
              return (
                <div key={i} className="reveal rounded-2xl bg-white p-7 ring-1 ring-brand-mist hover:ring-brand-sky/40 hover:shadow-card transition-all" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-mist text-brand-ocean"><Icon className="h-7 w-7" /></div>
                  <h3 className="mt-5 text-lg font-heading font-extrabold text-brand-navy">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-slate leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <SectionHeading center eyebrow="How It Works" title="Simple 4-Step Process" />
          <div className="mt-8 md:mt-12"><ProcessSteps steps={FINANCING.steps} /></div>
          <div className="text-center mt-10 reveal"><ApplyBtn /></div>
        </div>
      </section>

      {/* Apply band */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="reveal rounded-3xl bg-brand-navy text-white p-8 md:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center relative overflow-hidden">
            <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-brand-ocean/40 blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold">Ready to apply?</h2>
              <p className="mt-3 text-white/75">Click below to open our secure {FINANCING.provider} application. It only takes a few minutes to see what you qualify for - with no impact to start your project planning.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <ApplyBtn />
                <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 hover:bg-white/20 text-white font-heading font-bold ring-1 ring-white/25 transition-colors">
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
            <div className="relative rounded-2xl bg-white/5 ring-1 ring-white/15 p-6">
              <div className="flex items-center gap-3">
                <CreditCard className="h-8 w-8 text-brand-gold2" />
                <span className="font-heading font-extrabold text-lg">{FINANCING.provider} Financing</span>
              </div>
              <Link to="/estimate" className="mt-5 inline-flex items-center gap-2 text-brand-sky2 font-heading font-bold hover:gap-3 transition-all">
                Or request a free estimate first <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-brand-slate max-w-3xl reveal">{FINANCING.disclaimer}</p>
        </div>
      </section>
    </div>
  );
}
